import { io } from "socket.io-client";
import { basicurl } from "../Redux/ServiceUrl/url";
import SOCKET_EVENTS from "./events";
import { clearApiCache, handleRemoteInvalidation, setRealtimeProbe } from "../helper/apiCache";

/**
 * Single shared socket.io connection for the whole app.
 *
 * The token is read from sessionStorage on every (re)connect rather than being
 * captured once at import time, so a login in another tab or a refreshed token
 * is picked up without a full page reload.
 */
let socket = null;

const readToken = () => window.sessionStorage.getItem("user_token") || "";

export const getSocket = () => socket;

export const isConnected = () => Boolean(socket?.connected);

/** Creates the connection if needed and returns it. Safe to call repeatedly. */
export const connectSocket = () => {
  const token = readToken();
  if (!token) return null;

  if (socket) {
    if (!socket.connected) {
      socket.auth = { token: readToken() };
      socket.connect();
    }
    return socket;
  }

  socket = io(basicurl, {
    path: "/socket.io",
    auth: { token },
    transports: ["websocket", "polling"],
    withCredentials: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
    autoConnect: true,
  });

  // Always hand the freshest token to the server when reconnecting.
  socket.io.on("reconnect_attempt", () => {
    socket.auth = { token: readToken() };
  });

  // Data changed somewhere else (another user, another tab, a cron job): drop
  // the matching client-side cache and let open screens refetch. Registered
  // here rather than in a component so it is always active while a socket
  // exists, and the cache knows it can trust its own entries.
  setRealtimeProbe(isConnected);
  socket.on(SOCKET_EVENTS.DATA_INVALIDATED, handleRemoteInvalidation);

  socket.on("connect_error", (error) => {
    console.warn("[socket] connect_error:", error.message);
  });

  return socket;
};

/** Tears the connection down, e.g. on logout. */
export const disconnectSocket = () => {
  // Never leave one user's cached data behind for the next person to sign in.
  clearApiCache();
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};

/**
 * Emits an event. Falls back to a no-op (and reports it) when the socket is
 * down so callers can decide to use the REST endpoint instead.
 */
export const emit = (event, payload, ack) => {
  if (!socket?.connected) return false;
  socket.emit(event, payload, ack);
  return true;
};

/** Subscribes and returns an unsubscribe function, for use in useEffect. */
export const on = (event, handler) => {
  if (!socket) return () => {};
  socket.on(event, handler);
  return () => socket?.off(event, handler);
};

const socketClient = {
  connectSocket,
  disconnectSocket,
  emit,
  getSocket,
  isConnected,
  on,
};

export default socketClient;
